import React from "react";
import { Link } from "react-router-dom";
import InfoTooltip from "../../common/InfoTooltip";
import { ChartTips } from "./ChartTip";
import { tipMark, type TipContent } from "./chartTipFormat";

/**
 * The mock's small parts (built-v12 shared CSS). Each is one class in
 * ds.css; pages use these instead of styling text or controls themselves.
 */

/** The (i): a 15px navy-ringed circle; the text in the navy tooltip. */
export const InfoTip: React.FC<{ text: string }> = ({ text }) => (
  <InfoTooltip content={text}>
    <span className="ds-info" role="note" aria-label={text}>
      i
    </span>
  </InfoTooltip>
);

export interface ButtonProps {
  children: React.ReactNode;
  /** The one filled navy button of a section. */
  primary?: boolean;
  /** A route: renders a Link. */
  to?: string;
  /** A file or external URL: renders an <a>. */
  href?: string;
  download?: string | boolean;
  onClick?: () => void;
  disabled?: boolean;
  type?: "button" | "submit";
  "aria-label"?: string;
  title?: string;
}

/** Button (mock `.btn` / `.btn.primary`). */
export const Button: React.FC<ButtonProps> = ({
  children,
  primary,
  to,
  href,
  download,
  onClick,
  disabled,
  type = "button",
  ...rest
}) => {
  const p = primary ? "" : undefined;
  if (to && !disabled)
    return (
      <Link
        className="ds-btn"
        data-primary={p}
        to={to}
        onClick={onClick}
        aria-label={rest["aria-label"]}
        title={rest.title}
      >
        {children}
      </Link>
    );
  if (href && !disabled)
    return (
      <a
        className="ds-btn"
        data-primary={p}
        href={href}
        download={download}
        onClick={onClick}
        aria-label={rest["aria-label"]}
        title={rest.title}
      >
        {children}
      </a>
    );
  return (
    <button
      className="ds-btn"
      data-primary={p}
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={rest["aria-label"]}
      title={rest.title}
    >
      {children}
    </button>
  );
};

/** The one plain navy link of a section (mock `.show`), "… →". */
export const ShowLink: React.FC<{
  children: React.ReactNode;
  to?: string;
  onClick?: () => void;
}> = ({ children, to, onClick }) =>
  to ? (
    <Link className="ds-show" to={to} onClick={onClick}>
      {children}
    </Link>
  ) : (
    <button type="button" className="ds-show" onClick={onClick}>
      {children}
    </button>
  );

/** A small grey pill after a label (mock `.tag`, e.g. "expected"). */
export const Tag: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="ds-tag">{children}</span>
);

/** "New" badge after a style name (mock `.new`). */
export const NewBadge: React.FC<{ children?: React.ReactNode }> = ({
  children = "New",
}) => <span className="ds-new">{children}</span>;

/** A muted hint (mock `.hint`). */
export const Hint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="ds-hint">{children}</span>
);

/** Direction-coloured text (mock `.up` / `.down`), and the faint dash (`.soft`). */
export const Trend: React.FC<{
  dir: "up" | "down" | "flat";
  bold?: boolean;
  /** Flat in the faint ink (mock `.soft`). */
  faint?: boolean;
  title?: string;
  children: React.ReactNode;
}> = ({ dir, bold, faint, title, children }) => {
  const cls =
    dir === "up"
      ? "ds-up"
      : dir === "down"
        ? "ds-down"
        : faint
          ? "ds-soft"
          : undefined;
  return bold ? (
    <b className={cls} title={title}>
      {children}
    </b>
  ) : (
    <span className={cls} title={title}>
      {children}
    </span>
  );
};
export const Soft: React.FC<{ children?: React.ReactNode }> = ({
  children = "–",
}) => <span className="ds-soft">{children}</span>;

/** Coloured dot before a label (mock `.dot`). `color` is a token var. */
export const Dot: React.FC<{ color: string }> = ({ color }) => (
  <span className="ds-dot" style={{ background: color }} aria-hidden="true" />
);

/** Chart legend (mock `.legend`). */
export const Legend: React.FC<{
  items: Array<{
    label: React.ReactNode;
    color?: string;
    swatch?: React.ReactNode;
  }>;
}> = ({ items }) => (
  <div className="ds-legend">
    {items.map((it, i) => (
      <span key={i}>
        {it.swatch ?? <i style={{ background: it.color }} />}
        {it.label}
      </span>
    ))}
  </div>
);

/** The navy focus banner over a table (mock `.focus.on`). */
export const FocusBanner: React.FC<{
  children: React.ReactNode;
  onClear: () => void;
  clearLabel?: string;
}> = ({ children, onClear, clearLabel = "Show all" }) => (
  <div className="ds-focus" role="status">
    <span>{children}</span>
    <button type="button" onClick={onClear}>
      {clearLabel}
    </button>
  </div>
);

/** A share-of-total bar after a number (mock `.share`). */
export const ShareBar: React.FC<{
  pct: number;
  color: string;
  hideOnPhone?: boolean;
  /** The exact share on hover, tap and focus. */
  tip?: TipContent;
}> = ({ pct, color, hideOnPhone, tip }) => {
  const bar = (
    <span
      className={hideOnPhone ? "ds-share ds-hide-s" : "ds-share"}
      {...(tip ? tipMark(tip) : {})}
    >
      <span style={{ width: `${pct}%`, background: color }} />
    </span>
  );
  return tip ? <ChartTips>{bar}</ChartTips> : bar;
};

/** A horizontal bar (mock `.bar`). */
export const Bar: React.FC<{ pct: number; color?: string }> = ({
  pct,
  color,
}) => (
  <div className="ds-bar">
    <span style={{ width: `${pct}%`, background: color }} />
  </div>
);

/** A list of bar rows inside a card. */
export const BarList: React.FC<{
  children: React.ReactNode;
  tight?: boolean;
}> = ({ children, tight }) => (
  <div className="ds-barlist" data-tight={tight ? "" : undefined}>
    {children}
  </div>
);

/** One bar row (mock `.cat`: label, bar, value, change; `.st`: state, bar, value). */
export const BarRow: React.FC<{
  label: React.ReactNode;
  pct: number;
  value: React.ReactNode;
  change?: React.ReactNode;
  variant?: "cat" | "state";
}> = ({ label, pct, value, change, variant = "cat" }) => (
  <div className="ds-barrow" data-variant={variant}>
    <span>{label}</span>
    <Bar pct={pct} />
    <span className="num">{value}</span>
    {variant === "cat" && <span className="num">{change}</span>}
  </div>
);

/** A small stock meter (mock `.meter`). */
export const Meter: React.FC<{ pct: number; low?: boolean }> = ({
  pct,
  low,
}) => (
  <div className="ds-meter">
    <span data-low={low ? "" : undefined} style={{ width: `${pct}%` }} />
  </div>
);

/** Text clamped to two lines (mock `.clamp2`). */
export const Clamp2: React.FC<{
  children: React.ReactNode;
  title?: string;
}> = ({ children, title }) => (
  <span className="ds-clamp2" title={title}>
    {children}
  </span>
);

/** Hide on narrow screens (mock `.hide-m` ≤960, `.hide-s` ≤680). */
export const Hide: React.FC<{
  below: "m" | "s";
  children: React.ReactNode;
}> = ({ below, children }) => (
  <span className={below === "m" ? "ds-hide-m" : "ds-hide-s"}>{children}</span>
);

/** The page's footnote (mock `footer`). */
export const Footnote: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <footer className="ds-foot">{children}</footer>;

/** One verdict as a navy text link (mock `.vlink`): dot, words, small line. */
export const VerdictLink: React.FC<{
  text: React.ReactNode;
  sub?: React.ReactNode;
  color: string;
  to?: string | null;
}> = ({ text, sub, color, to }) => {
  const inner = (
    <>
      <i className="ds-vdot" style={{ background: color }} aria-hidden="true" />
      <span className="ds-vtx">
        <b>{text}</b>
        {sub != null && sub !== "" && <small>{sub}</small>}
      </span>
    </>
  );
  return to ? (
    <Link className="ds-vlink" to={to}>
      {inner}
    </Link>
  ) : (
    <span className="ds-vlink">{inner}</span>
  );
};

export type SizeLevel = "z" | "one" | "few" | "many";

/** Inventory by size (mock sizeMap): every cell the same size so columns line up. */
export const SizeMap: React.FC<{
  cells: Array<{
    size: string;
    units: React.ReactNode;
    level: SizeLevel;
    title?: string;
  }>;
  label?: string;
}> = ({ cells, label }) => (
  <div
    className="ds-sizes"
    aria-label={label}
    style={
      cells.length < 6
        ? ({ "--cols": Math.max(3, cells.length) } as React.CSSProperties)
        : undefined
    }
  >
    {cells.map((c, i) => (
      <span
        key={`${c.size}-${i}`}
        className="ds-cell"
        data-level={c.level}
        title={c.title}
      >
        <s>{c.size}</s>
        <b className="num">{c.units}</b>
      </span>
    ))}
  </div>
);

/** A muted line under a cell's main text (mock `div.hint`). */
export const SubLine: React.FC<{
  children: React.ReactNode;
  num?: boolean;
}> = ({ children, num }) => (
  <div className={num ? "ds-hint num" : "ds-hint"}>{children}</div>
);

/** A thin bar split into coloured shares (units by marketplace). `tip`
 *  names each share's exact figure on hover, tap and focus. */
export const SplitBar: React.FC<{
  parts: Array<{ key: string; pct: number; color: string }>;
  tip?: TipContent;
}> = ({ parts, tip }) => {
  const bar = (
    <span className="ds-split" {...(tip ? tipMark(tip) : {})}>
      {parts.map((p) => (
        <span key={p.key} style={{ width: `${p.pct}%`, background: p.color }} />
      ))}
    </span>
  );
  return tip ? <ChartTips>{bar}</ChartTips> : bar;
};

/** Inventory with its meter (mock `.stock`): "12 in stock" over a bar; low = amber. */
export const StockMeter: React.FC<{
  text: React.ReactNode;
  pct: number;
  low?: boolean;
  title?: string;
}> = ({ text, pct, low, title }) => (
  <div className="ds-stock">
    <span className="ds-stock-t" data-low={low ? "" : undefined}>
      {text}
    </span>
    <span className="ds-meter" title={title}>
      <span data-low={low ? "" : undefined} style={{ width: `${pct}%` }} />
    </span>
  </div>
);
